import React, { useEffect, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCServicesApi, RCCTooltip } from "react-common-components";
import SortableHandle from "src/components/Sortable/Handle";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import IFiltersActionsPanel, { ActionGroupers, IGroupersActions, IRefActionsPanelModalAddChangeDelete } from "src/pages/Actions/Panel/interface";
import ModalAddChangeDelete from "src/pages/Actions/Panel/components/modalAddChangeDelete";
import { standardErrorMessages } from "src/utils/errors";
import { useForm } from "react-hook-form";
import GrouperOfActions from "src/pages/Actions/Panel/grouperOfActions";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import { ContainerInput } from "./styles";
import { IFiltersIniciativesPanel } from "./interface";

interface IGroupersOfIniciatives{
    grouper: IGroupersActions;
    filters: IFiltersIniciativesPanel;
    actionsInCard: boolean;
		permissions: number;
    onCallbackDelete: (id: number) => void;
    onCallbackAdd: Function;
    onUpdateTitle: (id: number, title: string) => void;
		openModalReleaseActions?: (iniciativeId: number, iniciativeNome: string, onCallback?: Function) => void;
}

const GroupersOfIniciatives: React.FC<IGroupersOfIniciatives> = ({
	grouper,
	filters,
	actionsInCard,
	permissions,
	onCallbackDelete,
	onCallbackAdd,
	onUpdateTitle,
	openModalReleaseActions
}) => {
	const {translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const { administrator, superUser } = useAuth();

	const IniciativeInputDivRef = useRef<HTMLDivElement>(null);
	const modalActionRef = useRef<IRefActionsPanelModalAddChangeDelete>(null);

	const [inEdit, setInEdit] = useState(false);
	const [grouperData] = useState<IGroupersActions>(grouper);

	const { watch, register, setValue } = useForm();

	const editIniciative = () => {
		setInEdit(true);	
	};

	async function updateIniciative(id: number){
		try{
			const title = watch("ina001Titulo");
			if(!title || title === ""){
				addToast({
					title: translate("Erro"),
					description: translate("É necessário inserir um titulo"),
					color: "yellow"
				});
				return;
			}
			await RCCServicesApi.putRequest({
				method: "Iniciative/Update",
				urlParameters: {
					id,
					title
				}
			});

			onUpdateTitle(id, title);
            
			addToast({
				color: "green",
				description: translate("Iniciativa atualizada com sucesso"),
				title: translate("Sucesso")
			});
            
			setInEdit(false);
            
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar a iniciativa"), addToast);
		}
	}

	const deleteIniciative = (id: number, name: string) => {	
		confirm(
			translate("Exclusão da Iniciativa"), 
			`${translate("As ações vinculadas a iniciativa também serão excluídas")}. ${translate("Deseja realmente excluir a iniciativa")}: ${name} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Iniciative/Delete",
					urlParameters: {
						id
					}
				});
				addToast({
					color: "green",
					description: translate("Iniciativa excluída com sucesso"),
					title: translate("Sucesso")
				});
	
				onCallbackDelete(id);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir Iniciativa"), addToast);
			},
			true
		);
	};

	const editAccordionIniciative = (iniciative: IGroupersActions) => (
		<RCCGrid
			container
			justifyContent="space-between"
			styleInline="height: 45px; margin-bottom:20px"
			onClick={(e: any) => e.stopPropagation()}
		>
			<RCCGrid
				container
				item
				xs={10}
			>
				<ContainerInput
					ref={IniciativeInputDivRef}
				>
					<RCCInput
						name="ina001Titulo"
						label={translate("Nome da Iniciativa")}
						register={register}
						defaultValue={iniciative.title}
						maxLength={200}
					/>
				</ContainerInput>
			</RCCGrid>
			<RCCGrid
				container
				item
				justifyContent="flex-end"
				alignItems="center"
				xs={2}
			>
				<RCCTooltip
					text={translate("Atualizar")}
					color="green"
				>
					<RCCButton
						icon={{name: "check", color: "#FFF", size: 10}}
						color="green"
						size="xs"
						onClick={(e) => {e.stopPropagation(); updateIniciative(iniciative.id);}}
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Cancelar")}
					color="red"
				>
					<RCCButton
						icon={{name: "close", color: "#FFF", size: 10}}
						color="red"
						size="xs"
						onClick={(e) => {e.stopPropagation(); setInEdit(false); setValue("ina001Titulo", iniciative.title); }}
					/>
				</RCCTooltip>
			</RCCGrid>
		</RCCGrid>
	);

	useEffect(() => {
		IniciativeInputDivRef.current?.querySelectorAll("input")[0].focus();
	},[inEdit]);

	return (
		<>
			<GrouperOfActions 
				filters={filters as IFiltersActionsPanel}
				grouper={grouperData}
				groupersSelect={{
					group: ActionGroupers.INICIATIVE,
					delayed: "",
					origin: ""
				}}
				layoutList={actionsInCard ? "card" : "list"}
				onCallbackAdd={onCallbackAdd}
				header={inEdit && editAccordionIniciative(grouperData)}
			>

				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
					{( permissions >= 2 || administrator || superUser) && (
						<>
							<RCCTooltip
								text={translate("Adicionar Ações")}
								color="purple"
							>
								<RCCButton
									icon={{name: "plus", size: 12}}
									outlined
									color="purple"
									size="xs"
									layout="isRounded"
					    onClick={(e) => {e.stopPropagation(); modalActionRef.current?.openModalAddOrChange(undefined, () => onCallbackAdd(), undefined, undefined, {iniciativeId: grouperData.id});}}
								/>
							</RCCTooltip>
						</>
					)}											
				</CheckPermission>


				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
					{( permissions >= 2 || administrator || superUser) && (
						<>
							<SortableHandle>
								<RCCTooltip
									text={translate("Ordenar")}
									color="grey"
								>
									<RCCButton
										icon={{name: "arrows", size: 12}}
										outlined
										color="grey"
										size="xs"
										layout="isRounded"
										onClick={(e) => {e.stopPropagation();}}
									/>
								</RCCTooltip>
							</SortableHandle>

						</>	
					)}											
				</CheckPermission>
				

				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
					{( permissions >= 2 || administrator || superUser) && (
						<>
							{grouperData.draft !== 0 &&
								<RCCTooltip
									text={translate("Liberar Ações")}
									color="grey"
								>
									<RCCButton
										icon={{name: "listCheck", size: 18}}
										outlined
										color="grey"
										size="xs"
										layout="isRounded"
										onClick={(e: any) => {e.stopPropagation(); if(openModalReleaseActions) openModalReleaseActions(grouperData.id, grouperData.title, () => onCallbackAdd());}}
									/>
								</RCCTooltip>
							}
						</>
					)}											
				</CheckPermission>
			
				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
					{(permissions >= 2 || administrator || superUser) && (
						<RCCTooltip
							text={translate("Editar Iniciativa")}
							color="grey"
						>
							<RCCButton
								icon={{name: "pencil", size: 12}}
								outlined
								color="grey"
								size="xs"
								layout="isRounded"
								onClick={(e: any) => {e.stopPropagation(); editIniciative();}}
							/>
						</RCCTooltip>	
					)}											
				</CheckPermission>
				
				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
					{(permissions >= 2 || administrator || superUser) && (
						<RCCTooltip
							text={translate("Excluir Iniciativa")}
							color="red"
						>
							<RCCButton
								icon={{name: "trash", size: 12}}
								outlined
								color="red"
								size="xs"
								layout="isRounded"
								onClick={(e) => {e.stopPropagation(); deleteIniciative(grouperData.id, grouperData.title);}}
							/>
						</RCCTooltip>
					)}											
				</CheckPermission>
				
			</GrouperOfActions>

        	{/* Modal ADD inside subactions */}
			<ModalAddChangeDelete ref={modalActionRef} />
		</>
	);
};

export default GroupersOfIniciatives;
